import { useCallback, useEffect, useState } from 'react';

import { PageTitle } from '@components/PageTitle';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { useAuth } from '@contexts/auth';
import { useScroll } from '@contexts/scroll';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import { type CalculatorPediatrics, type Topics } from '@models/Calculator';
import { ChevronLeft, ChevronRightIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase, NumericFormat } from 'react-number-format';
import { Navigate } from 'react-router';
import { z } from 'zod';

import { useCalculator } from '../../context';
import Content from '../content';

const pediatricAntibioticDosage: CalculatorPediatrics = {
  name: 'Dose Antibióticos Pediatria',
  sections: [
    {
      title: 'Penicilinas',
      topics: [
        {
          title: 'AMOXICILINA SOL. ORAL',
          content: {
            applicationUnitMeasure: 'mL VO de 8/8 horas',
            doseDefault: 16.66,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 250,
              ml: 5,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '250mg/5mL',
                value: JSON.stringify({
                  mg: 250,
                  ml: 5,
                }),
              },
              {
                label: '400mg/5mL',
                value: JSON.stringify({
                  mg: 400,
                  ml: 5,
                }),
              },
              {
                label: '500mg/5mL',
                value: JSON.stringify({
                  mg: 500,
                  ml: 5,
                }),
              },
            ],
            multiplyDoseMax: 500,
            observations: [
              'Avaliar duração conforme condição a ser tratada (normalmente de 7 a 10 dias)',
            ],
            suggestions: [
              'Dose: 16,6 a 30mg/Kg/dose (50 a 90mg/Kg/dia)',
              'Dose máxima: 500mg/dose',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Amoxicilina',
            useDrugMl: true,
          },
        },
        {
          title: 'AMOX. + CLAVULANATO SOL. ORAL',
          content: {
            applicationUnitMeasure: 'ml VO de 8/8 horas',
            doseDefault: 16.66,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 250,
              ml: 5,
            }),
            drugOptions: [
              {
                label: '125+31,25mg/5mL',
                value: JSON.stringify({
                  mg: 125,
                  ml: 5,
                }),
              },
              {
                label: '200+28,5mg/5mL',
                value: JSON.stringify({
                  mg: 200,
                  ml: 5,
                }),
              },
              {
                label: '250+62,5mg/5mL',
                value: JSON.stringify({
                  mg: 250,
                  ml: 5,
                }),
              },
              {
                label: '400+57mg/5mL',
                value: JSON.stringify({
                  mg: 400,
                  ml: 5,
                }),
              },
            ],
            multiplyDoseMax: 500,
            observations: [
              'Avaliar duração conforme condição a ser tratada (normalmente de 7 a 10 dias)',
              'Deve ser orientado quanto a necessidade de armazenamento refrigerado (geladeira)',
            ],
            suggestions: [
              'Dose de amoxicilina: 16,6 a 30mg/Kg/dose (50 a 90mg/Kg/dia)',
              'Dose máxima: 500mg/dose de amoxicilina',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Amoxicilina + Clavulanato',
            useDrugMl: true,
          },
        },
        {
          title: 'PENICILINA BENZ. INJ.',
          content: {
            applicationUnitMeasure: 'U,IM',
            drugDefault: JSON.stringify({
              mg: 600_000,
              ml: 4,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '600.000 U',
                value: JSON.stringify({
                  mg: 600_000,
                  ml: 4,
                }),
              },
              {
                label: '1.200.000 U',
                value: JSON.stringify({
                  mg: 1_200_000,
                  ml: 4,
                }),
              },
            ],
            observations: [
              'Aplicar no QSL da nádega ou no vasto lateral da coxa em crianças pequenas',
            ],
            suggestions: [
              'Streptococcus do grupo A: 600.000 U (<= 27 kg) ou 1.200.000 U (> 27 kg), IM dose única',
              '_',
              'Sífilis recente (lesões primárias, lesões secundárias e latente recente - < 1 ano de evolução): 50.000 U/kg/dose (max 2,4 milhões U), IM dose única',
              '_',
              'Sífilis tardia (lesões terciárias, latente indeterminada, latente tardia - > 1 ano de evolução): 50.000 U/kg/dose (max 2,4 milhões U), IM a cada 1 semana, por 3 semanas',
            ],
            templateEight: true,
            title: 'Penicilina Benz.',
          },
        },
        {
          title: 'PENICILINA CRIST. PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL + 20-100mL SF 0,9% IV 4/4h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 16_700,
            doseUnitMeasure: 'U/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 1_000_000,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '1.000.000U',
                value: JSON.stringify({
                  mg: 1_000_000,
                }),
              },
              {
                label: '5.000.000U',
                value: JSON.stringify({
                  mg: 5_000_000,
                }),
              },
            ],
            multiplyDoseMax: 5_000_000,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 16.700U/Kg/dose (100.000 U/Kg/dia)',
              'Dose máxima: 5 milhões UI/dose',
            ],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Penicilina Crist. pó',
          },
        },
        {
          title: 'AMPICILINA INJ.',
          content: {
            applicationUnitMeasure: 'mL IV de 6/6 horas',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 50,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 500,
              ml: 5,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '500mg/5mL',
                value: JSON.stringify({
                  mg: 500,
                  ml: 5,
                }),
              },
              {
                label: '1000mg/5mL',
                value: JSON.stringify({
                  mg: 1000,
                  ml: 5,
                }),
              },
            ],
            multiplyDoseMax: 1000,
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 50mg/Kg/dose (200mg/Kg/dia)',
              'Dose máxima: 1g/dose',
            ],
            templateOne: true,
            title: 'Ampicilina',
          },
        },
        {
          title: 'AMOX. + CLAVULANATO PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL + 50-100mL SF 0,9% IV 8/8h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 30,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '500+100mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
              {
                label: '1000+200mg',
                value: JSON.stringify({
                  mg: 1000,
                }),
              },
            ],
            multiplyDoseMax: 1000,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 30mg/Kg/dose (90mg/Kg/dia)',
              'Dose máxima: 1g/dose de amoxicilina',
            ],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Amoxicilina + Clavulanato',
          },
        },
      ],
    },
    {
      title: 'Cefalosporinas',
      topics: [
        {
          title: 'CEFUROXIMA SOL. ORAL',
          content: {
            applicationUnitMeasure: 'mL VO de 12/12 horas',
            doseDefault: 15,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 250,
              ml: 5,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '250mg/5mL',
                value: JSON.stringify({
                  mg: 250,
                  ml: 5,
                }),
              },
            ],
            max: 10,
            suggestions: [
              'Dose: 15mg/Kg/dose (30mg/Kg/dia)',
              'Dose máxima: 500 mg/dose',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Cefuroxima',
            useDrugMl: true,
          },
        },
        {
          title: 'CEFALEXINA SOL. ORAL',
          content: {
            applicationUnitMeasure: 'mL VO de 6/6 horas',
            doseDefault: 12.5,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 250,
              ml: 5,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '250mg/5mL',
                value: JSON.stringify({
                  mg: 250,
                  ml: 5,
                }),
              },
              {
                label: '500mg/5mL',
                value: JSON.stringify({
                  mg: 500,
                  ml: 5,
                }),
              },
            ],
            multiplyDoseMax: 500,
            suggestions: [
              'Dose: 12,5mg/Kg/dose (50mg/Kg/dia)',
              'Dose máxima: 500 mg/dose',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Cefalexina',
            useDrugMl: true,
          },
        },
        {
          title: 'CEFTRIAXONA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL + 40-100mL SF 0,9% IV 12/12h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 50,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
              {
                label: '1000mg',
                value: JSON.stringify({
                  mg: 1000,
                }),
              },
            ],
            multiplyDoseMax: 1000,
            observations: ['Se for diluir, não usar Ringer'],
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 50mg/Kg/dose (100mg/Kg/dia)',
              'Dose máxima: 1.000mg/dose',
            ],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Ceftriaxona',
          },
        },
        {
          title: 'CEFOTAXIMA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL + 100mL SF 0,9% IV 6/6h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 25,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 1000,
              ml: 0,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '1000mg',
                value: JSON.stringify({
                  mg: 1000,
                  ml: 0,
                }),
              },
            ],
            multiplyDoseMax: 1000,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 25 a 50mg/Kg/dose (100 a 200mg/Kg/dia)',
              'Dose máxima: 1.000mg/dose',
            ],
            templateOne: true,
            title: 'Cefotaxima pó',
          },
        },
        {
          title: 'CEFUROXIMA INJ.',
          content: {
            applicationUnitMeasure: 'mL + 100mL SF 0,9% IV 8/8h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 50,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 750,
              ml: 6,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '750mg/6mL',
                value: JSON.stringify({
                  mg: 750,
                  ml: 6,
                }),
              },
            ],
            multiplyDoseMax: 1500,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Diluição: 1 amp + 100mL SF',
              'Dose: 33 a 50mg/Kg/dose (100 a 150mg/Kg/dia)',
              'Dose máxima: 1.500mg/dose',
            ],
            templateOne: true,
            title: 'Cefuroxima',
          },
        },
      ],
    },
    {
      title: 'Carbapenêmicos',
      topics: [
        {
          title: 'MEROPENEM PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL + 20mL SF 0,9% IV 8/8h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 10,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
              {
                label: '1000mg',
                value: JSON.stringify({
                  mg: 1000,
                }),
              },
            ],
            multiplyDoseMax: 1000,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 10 a 40mg/Kg/dose (30 a 120mg/Kg/dia)',
              'Dose máxima: 1.000mg/dose',
            ],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Meropenem',
          },
        },
        {
          title: 'IMIPENEM PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL + 100mL SF 0,9% IV 6/6h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 15,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
            ],
            multiplyDoseMax: 500,
            observations: ['Dose máxima: 2g/dia | infundir lento'],
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 15mg/Kg/dose (60mg/Kg/dia)',
              'Dose máxima: 500mg/dose',
            ],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Imipenem',
          },
        },
        {
          title: 'ERTAPENEM PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL + 100mL SF 0,9% IV 12/12h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 15,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 1000,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '1000mg',
                value: JSON.stringify({
                  mg: 1000,
                }),
              },
            ],
            multiplyDoseMax: 500,
            observations: [
              'Dose máxima: 1g/dia | infundir lento',
              'Dose máxima: 500mg/dose',
            ],
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: ['Dose: 15mg/Kg/dose (30mg/Kg/dia)'],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Ertapenem',
          },
        },
      ],
    },
    {
      title: 'Macrolídeos',
      topics: [
        {
          title: 'CLARITROMICINA SOL. ORAL',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'mL VO de 12/12 horas',
            doseDefault: 7.5,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 250,
              ml: 5,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '250mg/5mL',
                value: JSON.stringify({
                  mg: 250,
                  ml: 5,
                }),
              },
            ],
            max: 10,
            observations: ['Por 7 a 10 dias'],
            suggestions: [
              '7,5mg/Kg/dose (15mg/Kg/dia)',
              'Dose máxima: 500mg/dose',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Claritromicina',
            useDrugMl: true,
          },
        },
        {
          title: 'AZITROMICINA SOL. ORAL',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'mL VO de 24/24 horas',
            doseDefault: 10,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 200,
              ml: 5,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '200mg/5mL',
                value: JSON.stringify({
                  mg: 200,
                  ml: 5,
                }),
              },
            ],
            max: 12.5,
            observations: ['Por 5 dias'],
            suggestions: [
              '10mg/Kg/dose (10mg/Kg/dia)',
              'Dose máxima: 500mg/dose',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Azitromicina',
            useDrugMl: true,
          },
        },
      ],
    },
    {
      title: 'Sulfonamidas',
      topics: [
        {
          title: 'SULFAMETOXAZOL-TRIM SOL. ORAL',
          content: {
            applicationUnitMeasure: 'mL VO de 12/12 horas',
            doseDefault: 20,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 200,
              ml: 5,
              max: 20,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '200mg/5mL',
                value: JSON.stringify({
                  mg: 200,
                  ml: 5,
                  max: 20,
                }),
              },
              {
                label: '400mg/5mL',
                value: JSON.stringify({
                  mg: 400,
                  ml: 5,
                  max: 10,
                }),
              },
            ],
            suggestions: [
              '20mg/Kg/dose (40mg/Kg/dia)',
              'Dose máxima: 800mg/dose de sulfametoxazol',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Sulfametoxazol-trimetoprima',
            useDrugMl: true,
          },
        },
      ],
    },
    {
      title: 'Aminoglicosídeos',
      topics: [
        {
          title: 'GENTAMICINA INJ.',
          content: {
            applicationUnitMeasure: 'mL + 100mL SF 0,9% IV 8/8h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 1,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 20,
              ml: 1,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '20mg/1mL',
                value: JSON.stringify({
                  mg: 20,
                  ml: 1,
                }),
              },
              {
                label: '40mg/1mL',
                value: JSON.stringify({
                  mg: 40,
                  ml: 1,
                }),
              },
              {
                label: '80mg/2mL',
                value: JSON.stringify({
                  mg: 80,
                  ml: 2,
                }),
              },
            ],
            multiplyDoseMax: 100,
            observations: ['Infundir em 1h'],
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Reconstituir 1 ampola em 5mL SF 0,9%, e após diluir volume recomendado em 100mL SF 0,9%',
              'Dose: 1 a 2,5mg/Kg/dose (3 a 7,5mg/Kg/dia)',
              'Dose máxima: 100mg/dose',
            ],
            templateOne: true,
            title: 'Gentamicina',
          },
        },
        {
          title: 'AMICACINA INJ.',
          content: {
            applicationUnitMeasure: 'mL + 100mL SF 0,9% IV 12/12h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 7.5,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 100,
              ml: 2,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '100mg/2mL',
                value: JSON.stringify({
                  mg: 100,
                  ml: 2,
                }),
              },
              {
                label: '500mg/2mL',
                value: JSON.stringify({
                  mg: 500,
                  ml: 2,
                }),
              },
            ],
            multiplyDoseMax: 750,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Reconstituir 1 ampola em 5mL SF 0,9%, e após diluir volume recomendado em 100mL SF 0,9%',
              'Dose: 7,5mg/Kg/dose (15mg/Kg/dia)',
              'Dose máxima: 750mg/dose',
            ],
            templateOne: true,
            title: 'Amicacina',
          },
        },
      ],
    },
    {
      title: 'Glicopeptídeos',
      topics: [
        {
          title: 'VANCOMICINA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL + 200mL SF 0,9% IV 12/12h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 10,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
              {
                label: '1000mg',
                value: JSON.stringify({
                  mg: 1000,
                }),
              },
            ],
            multiplyDoseMax: 2000,
            observations: ['Infundir lento (1h)'],
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Diluição: 1 amp + 200mL SF ou SG 5%',
              'Dose: 10 a 20mg/Kg/dose (20 a 40 mg/Kg/dia)',
              'Dose máxima: 2.000mg/dose',
            ],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Vancomicina',
          },
        },
      ],
    },
    {
      title: 'Lincosamidas',
      topics: [
        {
          title: 'CLINDAMICINA SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL + 50mL SG 5% IV 6/6h',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 10,
            doseUnitMeasure: 'mg/kg/dose',
            drugDefault: JSON.stringify({
              mg: 300,
              ml: 2,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '300mg/2mL',
                value: JSON.stringify({
                  mg: 300,
                  ml: 2,
                }),
              },
              {
                label: '600mg/4mL',
                value: JSON.stringify({
                  mg: 600,
                  ml: 4,
                }),
              },
              {
                label: '900mg/6mL',
                value: JSON.stringify({
                  mg: 900,
                  ml: 6,
                }),
              },
            ],
            multiplyDoseMax: 600,
            salineDefault: 5,
            salineLabel: 'SG 5%',
            suggestions: [
              'Dose: 10mg/Kg/dose (40mg/Kg/dia)',
              'Dose máxima: 600mg/dose',
            ],
            templateOne: true,
            title: 'Clindamicina',
          },
        },
      ],
    },
  ],
};

const formSchema = z.object({
  weight: z.string().min(1, {
    message: errorsValidation.required,
  }),
  ageMonth: z.string().min(1, {
    message: errorsValidation.required,
  }),
  ageYear: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function PediatricAntibioticDosage() {
  const { user } = useAuth();
  const { onScrollTo } = useScroll();
  const [topics, setTopics] = useState<Topics[]>([]);
  const { ageMonth, ageYear, setAgeMonth, setAgeYear, setWeight, weight } =
    useCalculator();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      weight: weight.toFixed(1),
      ageMonth: String(ageMonth),
      ageYear: String(ageYear),
    },
  });

  const handleSetTopics = (topics: Topics[]) => {
    onScrollTo();
    setTopics(topics);
  };

  const handleClearTopics = () => {
    setTopics([]);
    onScrollTo();
  };

  const handleChangeWeight = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setWeight(valueParsed);
    },
    [setWeight],
  );

  const handleChangeAgeYear = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAgeYear(valueParsed);
    },
    [setAgeYear],
  );

  const handleChangeAgeMonth = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAgeMonth(valueParsed);
    },
    [setAgeMonth],
  );

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'weight' && value.weight) {
        handleChangeWeight(value.weight);
      }
      if (name === 'ageYear' && value.ageYear) {
        handleChangeAgeYear(value.ageYear);
      }
      if (name === 'ageMonth' && value.ageMonth) {
        handleChangeAgeMonth(value.ageMonth);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    setWeight(0);
    form.setValue('weight', '0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <div>
      <PageTitle hasBackButton title="Dose antibióticos pediatria" />
      <Form {...form}>
        <form
          className="bg-background sticky top-0 z-[9] py-4"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex justify-center gap-2">
            <FormField
              control={form.control}
              name="weight"
              render={({ field: { ref, ...rest } }) => (
                <FormItem className="flex flex-col items-center">
                  <FormLabel>Peso</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                  <FormDescription>Kg</FormDescription>
                </FormItem>
              )}
            />
            <div className="flex flex-col items-center justify-end gap-2">
              <Label>Idade</Label>
              <div className="flex flex-row gap-2">
                <FormField
                  control={form.control}
                  name="ageYear"
                  render={({ field: { ref, ...rest } }) => (
                    <FormItem className="flex flex-col items-center">
                      <FormControl>
                        <NumericFormat
                          className="[&_input]:w-20 [&_input]:text-center"
                          customInput={Input}
                          getInputRef={ref}
                          maxLength={2}
                          {...rest}
                        />
                      </FormControl>
                      <FormDescription>Anos</FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ageMonth"
                  render={({ field: { ref, ...rest } }) => (
                    <FormItem className="flex flex-col items-center">
                      <FormControl>
                        <NumericFormat
                          className="[&_input]:w-20 [&_input]:text-center"
                          customInput={Input}
                          getInputRef={ref}
                          maxLength={2}
                          {...rest}
                        />
                      </FormControl>
                      <FormDescription>Meses</FormDescription>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
          {!!topics.length && (
            <div className="mx-auto max-w-3xl">
              <button
                className="text-foreground mt-4 flex cursor-pointer flex-row items-center text-lg font-medium"
                onClick={handleClearTopics}
                title="Voltar"
                type="button">
                <ChevronLeft className="size-7" strokeWidth={2} />
                <h1>Voltar</h1>
              </button>
            </div>
          )}
        </form>
      </Form>
      <div
        className={cn(
          'mx-auto max-w-3xl transition-all duration-500',
          topics.length ? 'animate-out fade-out hidden' : 'animate-in fade-in',
        )}>
        {pediatricAntibioticDosage.sections.map((item) => (
          <button
            className="ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 flex w-full flex-1 cursor-pointer items-start justify-between gap-4 rounded-md py-4 text-left text-lg font-medium uppercase transition-all hover:underline hover:opacity-70 focus-visible:outline-1 focus-visible:ring-4 disabled:pointer-events-none disabled:opacity-50"
            key={item.title}
            onClick={() => handleSetTopics(item.topics)}>
            {item.title}
            <ChevronRightIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
          </button>
        ))}
      </div>
      <div
        className={cn(
          'mx-auto max-w-3xl transition-all duration-500',
          topics.length ? 'animate-in fade-in' : 'animate-out fade-out hidden',
        )}>
        {topics.map((item) => (
          <Content
            applicationText={item.content.applicationText}
            applicationUnitMeasure={item.content.applicationUnitMeasure}
            applicationUnitMeasure2={item.content.applicationUnitMeasure2}
            applicationUnitSubtitle={item.content.applicationUnitSubtitle}
            chargeDefault={item.content.chargeDefault}
            chargeTwoDefault={item.content.chargeTwoDefault}
            conditions={item.content.conditions}
            description={item.content.description}
            diluteDefault={item.content.diluteDefault}
            diluteText={item.content.diluteText}
            diluteUnitMeasureDefault={item.content.diluteUnitMeasureDefault}
            diluteUnitMeasureOptions={item.content.diluteUnitMeasureOptions}
            doseDefault={item.content.doseDefault}
            doseFixed={item.content.doseFixed}
            doseTwoCalc2={item.content.doseTwoCalc2}
            doseTwoDefault={item.content.doseTwoDefault}
            doseUnitMeasure={item.content.doseUnitMeasure}
            drugDefault={item.content.drugDefault}
            drugFieldWidth={item.content.drugFieldWidth}
            drugNameBig={item.content.drugNameBig}
            drugOptions={item.content.drugOptions}
            hideChargeThree={item.content.hideChargeThree}
            hideDoseTwoConditionText={item.content.hideDoseTwoConditionText}
            key={item.title}
            max={item.content.max}
            multiplyDoseMax={item.content.multiplyDoseMax}
            multiplyDoseTwoMax={item.content.multiplyDoseTwoMax}
            nebMax={item.content.nebMax}
            observations={item.content.observations}
            salineDefault={item.content.salineDefault}
            salineLabel={item.content.salineLabel}
            showBIC={item.content.showBIC}
            showDoseTwo={item.content.showDoseTwo}
            showSpeed={item.content.showSpeed}
            speedCalc2={item.content.speedCalc2}
            speedText={item.content.speedText}
            speedUnitMeasure={item.content.speedUnitMeasure}
            suggestions={item.content.suggestions}
            templateEight={item.content.templateEight}
            templateFive={item.content.templateFive}
            templateFour={item.content.templateFour}
            templateFourCalc2={item.content.templateFourCalc2}
            templateOne={item.content.templateOne}
            templateOneCalc2={item.content.templateOneCalc2}
            templateOneCalc3={item.content.templateOneCalc3}
            templateSeven={item.content.templateSeven}
            templateSevenCalc10={item.content.templateSevenCalc10}
            templateSevenCalc11={item.content.templateSevenCalc11}
            templateSevenCalc12={item.content.templateSevenCalc12}
            templateSevenCalc2={item.content.templateSevenCalc2}
            templateSevenCalc3={item.content.templateSevenCalc3}
            templateSevenCalc4={item.content.templateSevenCalc4}
            templateSevenCalc5={item.content.templateSevenCalc5}
            templateSevenCalc6={item.content.templateSevenCalc6}
            templateSevenCalc7={item.content.templateSevenCalc7}
            templateSevenCalc8={item.content.templateSevenCalc8}
            templateSevenCalc9={item.content.templateSevenCalc9}
            templateSix={item.content.templateSix}
            templateSixCalc2={item.content.templateSixCalc2}
            templateSixCalcDrops={item.content.templateSixCalcDrops}
            templateThree={item.content.templateThree}
            templateTwo={item.content.templateTwo}
            title={item.title}
            useDrugMl={item.content.useDrugMl}
          />
        ))}
      </div>
    </div>
  );
}

export default PediatricAntibioticDosage;
